import type { VoiceProfile } from "../types/app";
import { createProcessedAudio } from "../audio/webAudioChain";
import { normalizeApiError } from "../services/apiErrors";

export interface RealtimeConnection {
  disconnect: () => void;
  stopAssistant: () => void;
  interrupt: () => void;
  state: RTCPeerConnectionState;
}

export interface RealtimeCallbacks {
  onStatus: (status: string) => void;
  onError: (message: string) => void;
  onAssistantText: (text: string) => void;
  onSpeaking: (speaking: boolean) => void;
}

export async function connectRealtime(
  profile: VoiceProfile,
  callbacks: RealtimeCallbacks,
  maxMinutes: number,
): Promise<RealtimeConnection> {
  await assertRealtimeConfigured();
  if (!navigator.mediaDevices?.getUserMedia || !window.RTCPeerConnection) {
    throw new Error("Realtime API非対応のため、低コストモードへ切り替えてください。");
  }
  callbacks.onStatus("接続準備中");
  const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
  const pc = new RTCPeerConnection();
  const dataChannel = pc.createDataChannel("oai-events");
  const audioElement = new Audio();
  audioElement.autoplay = true;
  audioElement.muted = false;
  let timer: number | undefined;
  let closed = false;

  function close(): void {
    if (closed) return;
    closed = true;
    if (timer !== undefined) window.clearTimeout(timer);
    mic.getTracks().forEach((track) => track.stop());
    audioElement.pause();
    audioElement.srcObject = null;
    dataChannel.close();
    pc.close();
    callbacks.onSpeaking(false);
    callbacks.onStatus("切断");
  }

  pc.ontrack = async (event) => {
    callbacks.onSpeaking(true);
    try {
      await createProcessedAudio(event.streams[0], profile);
    } catch {
      audioElement.srcObject = event.streams[0];
      await audioElement.play().catch(() => undefined);
    }
  };
  mic.getTracks().forEach((track) => pc.addTrack(track, mic));
  dataChannel.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data as string) as { type?: string; delta?: string; transcript?: string };
      if (data.type?.includes("response.output_text.delta") && data.delta) callbacks.onAssistantText(data.delta);
      if (data.type?.includes("response.output_audio_transcript.delta") && data.delta) callbacks.onAssistantText(data.delta);
      if (data.type?.includes("response.done")) callbacks.onSpeaking(false);
    } catch {
      // Ignore non-JSON keepalive data.
    }
  };
  pc.onconnectionstatechange = () => callbacks.onStatus(statusLabel(pc.connectionState));

  try {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const unified = await fetch("/api/realtime/session", {
      method: "POST",
      headers: { "Content-Type": "application/sdp" },
      body: offer.sdp ?? "",
    });

    if (unified.ok && unified.headers.get("content-type")?.includes("application/sdp")) {
      const answer = await unified.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
    } else if (shouldUseClientSecretFallback(unified.status)) {
      const tokenResponse = await fetch("/api/realtime/session", { method: "POST", headers: { "Content-Type": "application/json" } });
      if (!tokenResponse.ok) throw await responseError(tokenResponse);
      const data = (await tokenResponse.json()) as { value?: string; client_secret?: { value?: string } };
      const token = data.value ?? data.client_secret?.value;
      if (!token) throw new Error("Realtime用シークレットを取得できませんでした。");
      const answerResponse = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/sdp" },
        body: offer.sdp ?? "",
      });
      if (!answerResponse.ok) throw await responseError(answerResponse);
      await pc.setRemoteDescription({ type: "answer", sdp: await answerResponse.text() });
    } else {
      throw await responseError(unified);
    }

    callbacks.onStatus("接続中");
    timer = window.setTimeout(
      () => {
        callbacks.onError("セッション時間超過です。");
        close();
      },
      Math.max(1, maxMinutes) * 60 * 1000,
    );
  } catch (error) {
    close();
    throw error;
  }

  function send(type: string): void {
    if (dataChannel.readyState === "open") dataChannel.send(JSON.stringify({ type }));
  }

  return {
    get state() {
      return pc.connectionState;
    },
    disconnect: close,
    stopAssistant: () => send("response.cancel"),
    interrupt: () => send("input_audio_buffer.clear"),
  };
}

async function assertRealtimeConfigured(): Promise<void> {
  let response: Response;
  try {
    response = await fetch("/api/health", { cache: "no-store" });
  } catch {
    return;
  }
  if (!response.ok) return;
  try {
    const health = (await response.json()) as { hasOpenAiKey?: boolean };
    if (health.hasOpenAiKey === false) {
      throw new Error("APIキーが設定されていません。Cloudflareの環境変数を確認してください。");
    }
  } catch (error) {
    if (error instanceof Error && error.message.startsWith("APIキーが設定されていません。")) throw error;
  }
}

function shouldUseClientSecretFallback(status: number): boolean {
  return status === 404 || status === 405 || status === 415 || status === 501;
}

async function responseError(response: Response): Promise<Error> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }
  return new Error(normalizeApiError(response.status, body).message);
}

function statusLabel(state: RTCPeerConnectionState): string {
  const labels: Record<RTCPeerConnectionState, string> = {
    new: "未接続",
    connecting: "接続中",
    connected: "接続中",
    disconnected: "再接続待ち",
    failed: "接続失敗",
    closed: "切断",
  };
  return labels[state];
}
